<template>
    <Dialog v-model:visible="visibleLocal" modal :header="t('billing.buy_credits.dialog_title')"
        :style="{ width: '680px' }" :closable="step === 1">
        <div class="flex flex-col gap-4">
            <InlineMessage severity="info">{{ t('billing.buy_credits.description') }}</InlineMessage>

            <div v-if="step === 1" class="flex flex-col gap-4">
                <div v-if="isLoadingPricingPlans" class="flex items-center justify-center gap-2 py-4">
                    <ProgressSpinner style="width:20px;height:20px" strokeWidth="4" />
                    <span>{{ t('setup.pricing.loading') }}</span>
                </div>

                <div v-else-if="topupOptions.length === 0">
                    <InlineMessage severity="warn">{{ t('billing.buy_credits.no_topups_available') }}</InlineMessage>
                </div>

                <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div v-for="option in topupOptions" :key="option.priceLookupKey"
                        class="border rounded-lg p-4 cursor-pointer transition-all"
                        :class="selectedOption?.priceLookupKey === option.priceLookupKey
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-surface-200 hover:border-primary/50'"
                        @click="selectedOption = option">
                        <div class="text-center">
                            <div class="text-2xl font-bold text-primary mb-1">
                                {{ formatNumber(option.credits) }}
                            </div>
                            <div class="text-sm text-color-secondary mb-3">
                                {{ t('billing.buy_credits.credits_label') }}
                            </div>
                            <div class="text-lg font-semibold">
                                {{ formatEuro(option.unitAmount) }}{{ currencySymbol }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="step === 2" class="flex flex-col gap-3">
                <div class="flex flex-col items-center gap-2">
                    <div class="flex items-center gap-2 text-sm text-gray-700">
                        <ProgressSpinner style="width:20px;height:20px" strokeWidth="4" />
                        <span>{{ t('billing.buy_credits.creating_checkout') }}</span>
                    </div>
                    <span class="text-xs text-color-secondary">{{ t('shared.loading_may_take_time') }}</span>
                </div>
                <InlineMessage v-if="errorMessage" severity="error">{{ errorMessage }}</InlineMessage>
            </div>

            <div v-else-if="step === 3" class="flex flex-col gap-3">
                <div class="flex flex-col items-center gap-2">
                    <div class="flex items-center gap-2 text-sm text-gray-700">
                        <ProgressSpinner style="width:20px;height:20px" strokeWidth="4" />
                        <span>{{ t('billing.buy_credits.redirecting') }}</span>
                    </div>
                    <span class="text-xs text-color-secondary">{{ t('shared.loading_may_take_time') }}</span>
                </div>
            </div>
        </div>

        <template #footer>
            <div v-if="step === 1" class="flex justify-end gap-2">
                <Button :label="t('shared.cancel')" severity="secondary" text @click="onCancel" :disabled="isWorking" />
                <Button :label="t('billing.buy_credits.buy')" :disabled="!canBuy || isWorking" @click="onBuy" />
            </div>
            <div v-else-if="step === 2" class="flex justify-end gap-2">
                <Button :label="t('shared.cancel')" severity="secondary" text :disabled="true" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InlineMessage from 'primevue/inlinemessage'
import ProgressSpinner from 'primevue/progressspinner'
import { storeToRefs } from 'pinia'

import { useBillingStore } from '@/stores/billing'
import { useTenantsStore } from '@/stores/tenants'
import { PriceLookupKeys } from '@/models/Billing'
import { TenantPlan } from '@/models/Tenant'
import { logger } from '@/utils/logger'
import { useSelfServeGate } from '@/composables/useSelfServeGate'

interface TopupOption {
    priceLookupKey: string
    priceId: string
    credits: number
    unitAmount: number
}

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ (e: 'update:visible', value: boolean): void }>()

const { t, locale } = useI18n()
const billingStore = useBillingStore()
const tenantsStore = useTenantsStore()
const { checkoutSession, pricingPlans, isLoadingPricingPlans } = storeToRefs(billingStore)
const { currentTenant } = storeToRefs(tenantsStore)
const { canSelfServeTopup, openContactSales } = useSelfServeGate()

const visibleLocal = ref(props.visible)
watch(() => props.visible, (v) => {
    // Self-serve gate (MYAC-837): route to Contact sales BEFORE the dialog paints
    // its top-up price grid, so no direct opener (assessments / internal-audit
    // cart buttons) can flash prices to a gated tenant. onBuy() stays as backstop.
    if (v && !canSelfServeTopup()) {
        emit('update:visible', false)
        openContactSales('topup')
        return
    }
    visibleLocal.value = v
})
watch(visibleLocal, (v) => {
    if (!v) resetForm()
    emit('update:visible', v)
})

const step = ref(1)
const isWorking = ref(false)
const errorMessage = ref<string | null>(null)
const selectedOption = ref<TopupOption | null>(null)

const activePlan = computed<TenantPlan | null>(() => (currentTenant.value?.plan as TenantPlan) || null)

const topupOptions = computed<TopupOption[]>(() => {
    if (!pricingPlans.value || !activePlan.value) return []
    const plan = pricingPlans.value.getByPlan(activePlan.value)
    if (!plan) return []

    const topupKeys = [PriceLookupKeys.TOPUP_S, PriceLookupKeys.TOPUP_M, PriceLookupKeys.TOPUP_L]
    const options: TopupOption[] = []
    for (const key of topupKeys) {
        const pricing = plan.getByPriceLookupKey(key)
        if (pricing?.price_id && pricing.price) {
            options.push({
                priceLookupKey: key,
                priceId: pricing.price_id,
                credits: pricing.price.metadata?.credits ?? 0,
                unitAmount: pricing.price.unit_amount ?? 0,
            })
        }
    }
    return options
})

onMounted(async () => {
    await tenantsStore.listTenants(false)
    await billingStore.loadActivePricingPlans()
})

watchEffect(() => {
    const url = checkoutSession.value?.url
    if (url) {
        step.value = 3
        window.location.replace(url)
    }
})

const canBuy = computed(() => !!selectedOption.value && !!currentTenant.value?.id && !!activePlan.value)

function resetForm() {
    step.value = 1
    isWorking.value = false
    errorMessage.value = null
    selectedOption.value = null
    // Clear any pending checkout session state to prevent stale redirects on reopen
    billingStore.checkoutSessionsSnapshot = null
    if (billingStore.checkoutSessionsUnsub) {
        billingStore.checkoutSessionsUnsub()
        billingStore.checkoutSessionsUnsub = null
    }
    if (billingStore.checkoutSessionRequestsUnsub) {
        billingStore.checkoutSessionRequestsUnsub()
        billingStore.checkoutSessionRequestsUnsub = null
    }
    billingStore.checkoutSessionRequestSnapshot = null
}

function onCancel() {
    visibleLocal.value = false
    resetForm()
}

function formatEuro(amountCents: number | null | undefined): string {
    const amount = (amountCents ?? 0) / 100
    return new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(amount)
}

function formatNumber(n: number): string {
    return new Intl.NumberFormat(locale.value).format(n)
}

const currencySymbol = computed(() => t('pricing.currency.eur'))

async function onBuy() {
    if (!canBuy.value || !selectedOption.value) return
    // Self-serve gate (MYAC-837): catch-all for every top-up entry point that
    // opens this dialog directly (assessments / internal-audit steps, …).
    if (!canSelfServeTopup()) {
        visibleLocal.value = false
        openContactSales('topup')
        return
    }
    try {
        errorMessage.value = null
        isWorking.value = true
        step.value = 2
        const tenant = currentTenant.value
        const plan = activePlan.value
        const priceId = selectedOption.value.priceId
        if (!tenant?.id || !plan || !priceId) throw new Error(t('billing.buy_credits.no_topups_available'))

        const quantities: Record<string, number> = { [priceId]: 1 }
        await billingStore.createCheckoutSessionRequest(tenant.id, plan, quantities)
    } catch (e) {
        logger.error('Error creating checkout for credits topup', e)
        errorMessage.value = (e as Error)?.message || t('billing.buy_credits.error_generic')
        isWorking.value = false
        step.value = 1
    }
}
</script>
